import { create } from 'zustand';
import { runCalculation } from '@rc/engine';
import type {
  CalculationResult,
  ClientProfile,
  Model,
  Scenario,
} from '@rc/engine/types';

interface ScenarioState {
  scenario: Scenario | null;
  model: Model | null;
  result: CalculationResult | null;
  dirty: boolean;
  setScenario: (s: Scenario, m: Model) => void;
  setModel: (m: Model) => void;
  setProfile: (patch: Partial<ClientProfile>) => void;
  setName: (name: string) => void;
  recalc: () => void;
  reset: () => void;
  markClean: () => void;
}

export const useScenarioStore = create<ScenarioState>()((set, get) => ({
  scenario: null,
  model: null,
  result: null,
  dirty: false,

  setScenario: (scenario, model) => {
    let result: CalculationResult | null = null;
    try {
      result = runCalculation(model, scenario.profile);
    } catch (err) {
      console.error('Failed to compute scenario result', err);
    }
    set({ scenario, model, result, dirty: false });
  },

  setModel: (model) =>
    set((s) => {
      if (!s.scenario) return { model };
      let result: CalculationResult | null = s.result;
      try {
        result = runCalculation(model, s.scenario.profile);
      } catch (err) {
        console.error('Recalculation failed after model change', err);
      }
      return {
        model,
        result,
        scenario: { ...s.scenario, modelId: model.id },
        dirty: true,
      };
    }),

  setProfile: (patch) =>
    set((s) => {
      if (!s.scenario || !s.model) return s;
      const next: Scenario = {
        ...s.scenario,
        profile: { ...s.scenario.profile, ...patch },
      };
      let result: CalculationResult | null = s.result;
      try {
        result = runCalculation(s.model, next.profile);
      } catch (err) {
        console.error('Recalculation failed', err);
      }
      return { scenario: next, result, dirty: true };
    }),

  setName: (name) =>
    set((s) => (s.scenario ? { scenario: { ...s.scenario, name }, dirty: true } : s)),

  recalc: () => {
    const { scenario, model } = get();
    if (!scenario || !model) return;
    try {
      const result = runCalculation(model, scenario.profile);
      set({ result });
    } catch (err) {
      console.error('Recalculation failed', err);
    }
  },

  reset: () => set({ scenario: null, model: null, result: null, dirty: false }),

  markClean: () => set({ dirty: false }),
}));
